import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ApiError } from '../api/client';
import { createFlowAOrder, createFlowBOrder } from '../api/orders';
import type { BankType, FlowType } from '../types/api';
import { BANK_TYPE_MAP } from '../types/api';
import { formatAmount } from '../lib/format';

const BANK_OPTIONS: BankType[] = ['004', '013', '822'];

interface FormState {
  customerName: string;
  prodDesc: string;
  amount: string;
  expireDays: string;
  bankType: BankType;
}

const INITIAL: FormState = {
  customerName: '',
  prodDesc: '',
  amount: '',
  expireDays: '7',
  bankType: '822', // 流程 B 不送，但欄位仍保留避免型別錯亂
};

type Errors = Partial<Record<keyof FormState, string>>;

function validate(state: FormState, flow: FlowType): Errors {
  const errs: Errors = {};
  if (!state.customerName.trim()) errs.customerName = '必填';
  else if (state.customerName.length > 30) errs.customerName = '最多 30 字';

  if (!state.prodDesc.trim()) errs.prodDesc = '必填';
  else if (state.prodDesc.length > 50) errs.prodDesc = '最多 50 字';

  const amt = parseInt(state.amount, 10);
  if (!state.amount || !Number.isInteger(amt)) errs.amount = '請輸入整數';
  else if (amt < 1 || amt > 50000) errs.amount = '範圍 1 ~ 50,000';

  const days = parseInt(state.expireDays, 10);
  if (!state.expireDays || !Number.isInteger(days)) errs.expireDays = '請輸入整數';
  else if (days < 1 || days > 180) errs.expireDays = '範圍 1 ~ 180';

  if (flow === 'A' && !BANK_OPTIONS.includes(state.bankType)) errs.bankType = '請選銀行';

  return errs;
}

export function NewOrderPage() {
  const navigate = useNavigate();
  // 流程 A 暫時隱藏（CF Workers IP 不固定無法滿足 PAYUNi 白名單）→ 固定走 B
  // 用 useState 保留變數型別為 FlowType（不窄化為 'B'），讓 `flow === 'A'` 的分支
  // 程式碼能保留供未來流程 A 重啟用（docs/plan-flow-a-relay.md）
  const [flow] = useState<FlowType>('B');
  const [form, setForm] = useState<FormState>(INITIAL);
  const [errors, setErrors] = useState<Errors>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
    setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const errs = validate(form, flow);
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setSubmitting(true);
    try {
      const common = {
        customerName: form.customerName.trim(),
        prodDesc: form.prodDesc.trim(),
        amount: parseInt(form.amount, 10),
        expireDays: parseInt(form.expireDays, 10),
      };

      const result =
        flow === 'A'
          ? await createFlowAOrder({ ...common, bankType: form.bankType })
          : await createFlowBOrder(common);

      navigate(`/orders/${result.id}`, { replace: true });
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.code === 'INVALID_PAYLOAD' && e.details) {
          const beErrors: Errors = {};
          for (const [k, v] of Object.entries(e.details)) {
            if (k in INITIAL) beErrors[k as keyof FormState] = String(v);
          }
          setErrors(beErrors);
        }
        setSubmitError(`${e.code}: ${e.message}`);
      } else {
        setSubmitError((e as Error).message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const previewAmount = (() => {
    const n = parseInt(form.amount, 10);
    return Number.isInteger(n) && n > 0 ? formatAmount(n) : '—';
  })();

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        <Link to="/" className="text-blue-600 hover:underline">
          ← 訂單列表
        </Link>
      </div>

      {/* 流程切換 tab（流程 A 暫時隱藏 — Cloudflare Workers Anycast IP 無法滿足
          PAYUNi「幕後取號必須白名單」要求；待家用 PAYUNi proxy 完成後重啟，
          詳見 docs/plan-flow-a-relay.md） */}
      <div className="rounded-lg border bg-white p-3">
        <div className="text-sm font-medium text-gray-700">流程 B：UPP 連結 → 客戶自選銀行</div>
        <div className="mt-0.5 text-xs text-gray-500">建單後將 paymentUrl 傳給客戶，客戶於 PAYUNi 頁面選銀行取號</div>
      </div>

      <div className="rounded-lg border bg-white p-5 shadow-sm">
        <form onSubmit={handleSubmit} noValidate className="space-y-4">
          <Field label="客戶姓名" error={errors.customerName} required>
            <input
              type="text"
              autoFocus
              value={form.customerName}
              onChange={(e) => update('customerName', e.target.value)}
              maxLength={30}
              placeholder="王小明"
              className={inputCls(errors.customerName)}
            />
          </Field>

          <Field label="訂單描述" error={errors.prodDesc} hint={flow === 'A' ? '會印在 PDF 上' : '會傳到 PAYUNi 對帳'} required>
            <input
              type="text"
              value={form.prodDesc}
              onChange={(e) => update('prodDesc', e.target.value)}
              maxLength={50}
              placeholder="A4 月曆訂製 50 本"
              className={inputCls(errors.prodDesc)}
            />
          </Field>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="金額（NT$）" error={errors.amount} required>
              <input
                type="number"
                inputMode="numeric"
                value={form.amount}
                onChange={(e) => update('amount', e.target.value)}
                min={1}
                max={50000}
                placeholder="1200"
                className={inputCls(errors.amount)}
              />
              <div className="mt-1 text-xs text-gray-500">預覽：{previewAmount}</div>
            </Field>

            <Field label="繳費期限（天）" error={errors.expireDays} hint="建議 7" required>
              <input
                type="number"
                inputMode="numeric"
                value={form.expireDays}
                onChange={(e) => update('expireDays', e.target.value)}
                min={1}
                max={180}
                className={inputCls(errors.expireDays)}
              />
            </Field>
          </div>

          {flow === 'A' && (
            <Field label="收款銀行" error={errors.bankType} required>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {BANK_OPTIONS.map((bt) => (
                  <label
                    key={bt}
                    className={`flex cursor-pointer items-center gap-2 rounded border px-3 py-3 text-sm ${
                      form.bankType === bt
                        ? 'border-stamp bg-rose-50 text-stamp'
                        : 'border-gray-300 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="bankType"
                      value={bt}
                      checked={form.bankType === bt}
                      onChange={() => update('bankType', bt)}
                      className="h-4 w-4"
                    />
                    <span className="font-medium">{BANK_TYPE_MAP[bt]}</span>
                    <span className="text-xs text-gray-400">({bt})</span>
                  </label>
                ))}
              </div>
            </Field>
          )}

          {flow === 'B' && (
            <div className="rounded border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
              客戶將在 PAYUNi 付款頁自選銀行。系統會產生短連結，可在訂單詳情頁複製貼到 LINE / Email 給客戶。
            </div>
          )}

          {submitError && (
            <div className="rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
              {submitError}
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
            <Link
              to="/"
              className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            >
              取消
            </Link>
            <button
              type="submit"
              disabled={submitting}
              className="rounded bg-stamp px-5 py-2 text-sm font-medium text-white hover:bg-rose-800 disabled:opacity-50"
            >
              {submitting ? '建立中…' : flow === 'A' ? '建立訂單並取號' : '建立訂單並產生連結'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function inputCls(err?: string) {
  return [
    'w-full rounded border px-3 py-2.5 text-sm',
    'min-h-[44px]',
    err ? 'border-rose-300 bg-rose-50' : 'border-gray-300 bg-white',
  ].join(' ');
}

function Field({
  label,
  error,
  hint,
  required,
  children,
}: {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 flex items-baseline justify-between text-sm font-medium text-gray-700">
        <span>
          {label}
          {required && <span className="ml-0.5 text-rose-600">*</span>}
        </span>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </span>
      {children}
      {error && <span className="mt-1 block text-xs text-rose-700">{error}</span>}
    </label>
  );
}
